/**
 * Cobro con tarjeta — PCI por delegación de verdad (SAQ-A).
 *
 *  - live: **Bricks** de MercadoPago. Los campos de la tarjeta son iframes de
 *    MP: el número NUNCA entra a nuestro DOM ni a nuestro JavaScript. Además
 *    resuelve lo que un formulario propio no puede: banco emisor (obligatorio
 *    en México) y meses sin intereses reales.
 *  - demo: formulario propio simulado, sin red, para ver el flujo sin llaves.
 *
 * Detalle de montaje: el widget es un Web Component con Shadow DOM y el SDK de
 * MP monta por `document.getElementById`. Por eso el Brick vive en una capa
 * propia colgada de `document.body`, no dentro del shadow root.
 */

const MP_PUBLIC_KEY = import.meta.env.VITE_MP_PUBLIC_KEY as string | undefined

/** true si hay llave pública de MP → tokenización real; si no, demo. */
export const hasMp = Boolean(MP_PUBLIC_KEY)

export interface CardInput {
  number: string
  name: string
  expMonth: string
  expYear: string
  cvv: string
}

export interface CardToken {
  token: string
  paymentMethodId: string
  lastFour: string
}

/** Lo que hace falta para cobrar, venga del Brick o del formulario demo. */
export interface PaidPayload {
  token: string
  paymentMethodId: string
  /** Banco emisor: MercadoPago lo exige en México. */
  issuerId?: string
  installments: number
  email: string
  /** WhatsApp del titular, escrito en el paso de pago. */
  phone?: string
  name: string
  lastFour?: string
  identification?: { type: string; number: string }
}

export type CardBrand = 'visa' | 'master' | 'amex' | 'unknown'

/** Valida el número con el algoritmo de Luhn (mismo check que usa la pasarela). */
export function luhnValid(raw: string): boolean {
  const num = raw.replace(/\D/g, '')
  if (num.length < 13) return false
  let sum = 0
  let alt = false
  for (let i = num.length - 1; i >= 0; i--) {
    let d = Number(num[i])
    if (alt) {
      d *= 2
      if (d > 9) d -= 9
    }
    sum += d
    alt = !alt
  }
  return sum % 10 === 0
}

/** Marca de la tarjeta por prefijo (para el `payment_method_id` y el ícono). */
export function detectBrand(raw: string): CardBrand {
  const n = raw.replace(/\D/g, '')
  if (/^4/.test(n)) return 'visa'
  if (/^(5[1-5]|2[2-7])/.test(n)) return 'master'
  if (/^3[47]/.test(n)) return 'amex'
  return 'unknown'
}

const BRAND_TO_MP: Record<CardBrand, string> = {
  visa: 'visa',
  master: 'master',
  amex: 'amex',
  unknown: 'visa',
}

/** La expiración es válida si es un mes real y no está en el pasado. */
export function expiryValid(month: string, year: string): boolean {
  const m = Number(month)
  const y = Number(year.length === 2 ? `20${year}` : year)
  if (!m || m < 1 || m > 12 || !y) return false
  const now = new Date()
  const end = new Date(y, m, 0, 23, 59, 59) // último día del mes
  return end.getTime() >= now.getTime()
}

function delay(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms))
}

/* ---------- Demo: token simulado (sin llaves, sin red) ---------- */

/**
 * Tokeniza en modo demo. En vivo NO se usa: ahí el número de tarjeta jamás
 * pasa por nuestro código — lo capturan los iframes del Brick.
 */
export async function tokenizeCard(card: CardInput): Promise<CardToken> {
  const digits = card.number.replace(/\D/g, '')
  const lastFour = digits.slice(-4)
  await delay(650) // simula el ida y vuelta de la tokenización
  return {
    token: `demo-tok-${lastFour}-${digits.length}`,
    paymentMethodId: BRAND_TO_MP[detectBrand(digits)],
    lastFour,
  }
}

/* ---------- Bricks de MercadoPago (en vivo) ---------- */

/** Datos que devuelve el Brick al enviar el formulario. */
interface BrickFormData {
  token: string
  issuer_id?: string
  payment_method_id: string
  installments?: number
  payer?: { email?: string; identification?: { type?: string; number?: string } }
}

interface BrickController {
  unmount(): void
}

interface MpSdk {
  bricks(): {
    create(
      brick: 'cardPayment',
      containerId: string,
      settings: Record<string, unknown>,
    ): Promise<BrickController>
  }
}

interface MpCtor {
  new (publicKey: string, opts?: { locale?: string }): MpSdk
}

declare global {
  interface Window {
    MercadoPago?: MpCtor
  }
}

let mpInstance: MpSdk | null = null

/** Carga el SDK de MP una sola vez y devuelve una instancia con la llave pública. */
async function getMp(): Promise<MpSdk> {
  if (mpInstance) return mpInstance
  if (!window.MercadoPago) {
    await new Promise<void>((resolve, reject) => {
      const s = document.createElement('script')
      s.src = 'https://sdk.mercadopago.com/js/v2'
      s.onload = () => resolve()
      s.onerror = () => reject(new Error('No se pudo cargar el SDK de MercadoPago.'))
      document.head.appendChild(s)
    })
  }
  if (!window.MercadoPago) throw new Error('SDK de MercadoPago no disponible.')
  mpInstance = new window.MercadoPago(MP_PUBLIC_KEY!, { locale: 'es-MX' })
  return mpInstance
}

const CAPA_ID = 'tc-mp-capa'
const ESTILOS_ID = 'tc-mp-estilos'

/**
 * Estilos de la capa. Van en `document.head` (no en el shadow root) porque el
 * Brick vive fuera del componente; por eso todo está prefijado con `tc-mp-`.
 */
function asegurarEstilos(): void {
  if (document.getElementById(ESTILOS_ID)) return
  const el = document.createElement('style')
  el.id = ESTILOS_ID
  el.textContent = `
    .tc-mp-capa { position: fixed; inset: 0; z-index: 2147483000; display: flex;
      align-items: flex-end; justify-content: center; background: rgba(11,31,51,.55);
      backdrop-filter: blur(2px); font-family: system-ui, -apple-system, "Segoe UI", sans-serif; }
    @media (min-width: 620px) { .tc-mp-capa { align-items: center; } }
    .tc-mp-caja { background: #fff; width: 100%; max-width: 460px; max-height: 92vh; overflow-y: auto;
      border-radius: 18px 18px 0 0; padding: 18px 18px 22px; box-shadow: 0 -8px 40px rgba(0,0,0,.25); }
    @media (min-width: 620px) { .tc-mp-caja { border-radius: 18px; } }
    .tc-mp-top { display: flex; align-items: center; justify-content: space-between; margin-bottom: 4px; }
    .tc-mp-tit { font-weight: 700; font-size: 15px; color: #0b1f33; }
    .tc-mp-x { border: 0; background: transparent; font-size: 22px; line-height: 1; cursor: pointer; color: #6b7b8c; padding: 2px 6px; }
    .tc-mp-monto { font-size: 13px; color: #6b7b8c; margin-bottom: 12px; }
    .tc-mp-err { color: #b3261e; font-size: 12.5px; margin-top: 10px; }
    .tc-mp-campo { margin-bottom: 14px; }
    .tc-mp-campo label { display: block; font-size: 12px; color: #6b7b8c; margin-bottom: 5px; }
    .tc-mp-campo input { width: 100%; padding: 10px 12px; font-size: 14px; font-family: inherit;
      border: 1px solid #d5d9de; border-radius: 6px; box-sizing: border-box; }
    .tc-mp-campo input:focus { outline: 2px solid #1668e3; outline-offset: -1px; }
    .tc-mp-campo .tc-mp-err { margin-top: 6px; }
    .tc-mp-cargando { color: #6b7b8c; font-size: 13px; padding: 26px 0; text-align: center; }
    .tc-mp-err { color: #10203a; font-size: 13.5px; line-height: 1.5; padding: 6px 2px 2px; }
    .tc-mp-err b { color: #10203a; }
    .tc-mp-err p { margin: 8px 0; color: #5b6b82; }
    .tc-mp-err ul { margin: 8px 0; padding-left: 18px; color: #5b6b82; }
    .tc-mp-err li { margin: 4px 0; }
  `
  document.head.appendChild(el)
}

export interface BrickOpts {
  amount: number
  currency: string
  /** Correo del cliente si ya lo tenemos (el Brick lo pide si no). */
  email?: string
  /** Tope de mensualidades a ofrecer (meses sin intereses de UVAC). */
  maxInstallments?: number
}

function money(n: number, c: string): string {
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: c }).format(n)
}

/**
 * Abre el Brick de tarjeta en una capa sobre el chat y resuelve con los datos
 * del pago. Devuelve null si el cliente cierra sin pagar.
 *
 * El `onSubmit` del Brick espera una promesa: la resolvemos en cuanto tenemos
 * los datos, y el cobro real lo hace el backend (`reservationBook`). Así el
 * navegador nunca decide si se cobra.
 */
export async function openCardBrick(opts: BrickOpts): Promise<PaidPayload | null> {
  const mp = await getMp()
  asegurarEstilos()

  const capa = document.createElement('div')
  capa.className = 'tc-mp-capa'
  capa.id = CAPA_ID
  const contenedorId = `tc-mp-brick-${Date.now()}`
  capa.innerHTML = `
    <div class="tc-mp-caja" role="dialog" aria-modal="true" aria-label="Pago con tarjeta">
      <div class="tc-mp-top">
        <span class="tc-mp-tit">Pago seguro</span>
        <button class="tc-mp-x" aria-label="Cerrar">×</button>
      </div>
      <div class="tc-mp-monto">${money(opts.amount, opts.currency)} · si tu reserva no se confirma, no te quedas con el cargo</div>
      <div class="tc-mp-campo">
        <label for="tc-mp-titular">Nombre completo de quien viaja</label>
        <input id="tc-mp-titular" type="text" autocomplete="name" placeholder="Como aparece en su identificación" />
        <div class="tc-mp-err" id="tc-mp-titular-err" style="display:none">
          Necesitamos el nombre para emitir la reserva.
        </div>
      </div>
      <div id="${contenedorId}"></div>
      <div class="tc-mp-cargando">Cargando pago seguro…</div>
    </div>`
  document.body.appendChild(capa)

  return new Promise<PaidPayload | null>((resolve) => {
    let controlador: BrickController | null = null
    let cerrado = false

    const cerrar = (valor: PaidPayload | null): void => {
      if (cerrado) return
      cerrado = true
      try {
        controlador?.unmount()
      } catch {
        /* si el Brick ya se fue, da igual */
      }
      capa.remove()
      resolve(valor)
    }

    capa.querySelector('.tc-mp-x')?.addEventListener('click', () => cerrar(null))
    capa.addEventListener('click', (e) => {
      if (e.target === capa) cerrar(null)
    })

    /**
     * Cuando el pago no abre, decir por qué y qué hacer.
     *
     * El caso real: un navegador con protección estricta contra rastreo —o
     * simplemente una ventana privada de Firefox, que la activa sola— bloquea
     * los archivos de MercadoPago. El formulario se queda en "Cargando pago
     * seguro…" para siempre, y el cliente se va pensando que el sitio no sirve.
     *
     * Ni el SDK ni nosotros podemos saltarnos ese bloqueo: los recursos vienen
     * de otro dominio y el navegador decide. Lo único honesto es explicarlo y
     * dar la salida.
     */
    function noAbrio(): void {
      if (cerrado) return
      const caja = capa.querySelector('.tc-mp-caja')
      if (!caja) return
      caja.innerHTML =
        '<div class="tc-mp-err">' +
        '<b>No se pudo abrir el pago seguro.</b>' +
        '<p>Casi siempre es el navegador bloqueando a MercadoPago. Prueba una de estas:</p>' +
        '<ul>' +
        '<li>Abrir esta página en una <b>ventana normal</b>, no privada ni de incógnito.</li>' +
        '<li>Desactivar el bloqueador de rastreadores o de anuncios <b>para este sitio</b>.</li>' +
        '<li>Probar en otro navegador.</li>' +
        '</ul>' +
        '<p>Tu viaje sigue apartado en el chat y <b>no se te cobró nada</b>. Si prefieres, ' +
        'escríbele al asistente y un asesor te pasa una liga de pago.</p>' +
        '</div>'
    }

    // Si el formulario no llegó a estar listo, no se queda colgado en silencio.
    const vigilante = setTimeout(noAbrio, 12000)

    void mp
      .bricks()
      .create('cardPayment', contenedorId, {
        initialization: {
          amount: opts.amount,
          ...(opts.email ? { payer: { email: opts.email } } : {}),
        },
        customization: {
          paymentMethods: { maxInstallments: opts.maxInstallments ?? 12 },
          visual: { style: { theme: 'default' } },
        },
        callbacks: {
          onReady: () => {
            clearTimeout(vigilante)
            capa.querySelector('.tc-mp-cargando')?.remove()
          },
          onSubmit: (datos: BrickFormData) =>
            new Promise<void>((listo, falla) => {
              // El nombre NO lo pide MercadoPago (al banco no le hace falta),
              // pero el hotel sí lo necesita para emitir la reserva. Sin él, el
              // huésped llega al mostrador y no aparece.
              const campo = capa.querySelector<HTMLInputElement>('#tc-mp-titular')
              const nombre = (campo?.value ?? '').trim()
              const err = capa.querySelector<HTMLElement>('#tc-mp-titular-err')
              if (nombre.split(/\s+/).filter(Boolean).length < 2) {
                if (err) err.style.display = 'block'
                campo?.focus()
                falla(new Error('Falta el nombre del titular'))
                return
              }
              cerrar({
                token: datos.token,
                paymentMethodId: datos.payment_method_id,
                issuerId: datos.issuer_id,
                installments: datos.installments ?? 1,
                email: datos.payer?.email ?? opts.email ?? '',
                name: nombre,
                identification:
                  datos.payer?.identification?.type && datos.payer.identification.number
                    ? {
                        type: datos.payer.identification.type,
                        number: datos.payer.identification.number,
                      }
                    : undefined,
              })
              listo()
            }),
          onError: (e: unknown) => {
            // El Brick pinta sus propios errores de validación; aquí solo
            // dejamos rastro de los que rompen el flujo.
            console.warn('[mp] error del Brick', e)
          },
        },
      })
      .then((c) => {
        controlador = c
      })
      .catch(() => {
        clearTimeout(vigilante)
        // Sin cierre automático: el mensaje explica qué hacer y hay que poder
        // leerlo. Cerrarle la ventana en la cara a alguien que está intentando
        // pagar es la peor manera de perder una venta.
        noAbrio()
      })
  })
}
